import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useDashboardState } from "@/hooks/use-dashboard-state";
import { useDashboardAnalytics } from "@/hooks/use-dashboard-analytics";
import { useSmartAlerts } from "@/hooks/use-smart-alerts";
import { useSubscription } from "@/hooks/use-subscription";
import { DashboardKpiHeader } from "@/components/dashboard/DashboardKpiHeader";
import { DashboardAlertsBanner } from "@/components/dashboard/DashboardAlertsBanner";
import { DashboardMobileTabs } from "@/components/dashboard/DashboardMobileTabs";
import { DashboardDesktopLayout } from "@/components/dashboard/DashboardDesktopLayout";
import { DashboardFab } from "@/components/dashboard/DashboardFab";
import { DashboardModals } from "@/components/dashboard/DashboardModals";
import { FeedbackDialog } from "@/components/shared/FeedbackDialog";
import { UpgradeModal } from "@/components/subscription/UpgradeModal";

const Dashboard = () => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const state = useDashboardState();
  const {
    monthlyData,
    categoryData,
    monthlyComparison,
    isLoading: analyticsLoading,
  } = useDashboardAnalytics();
  const { alerts, isLoading: alertsLoading, dismissAlert } = useSmartAlerts();
  const { canAccess, isWithinLimit, getLimit } = useSubscription();

  if (state.loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const importLocked = !canAccess('statement_import');
  const creditCardLocked = !canAccess('credit_card_access');

  const accountCounts = {
    checking: state.bankAccounts.filter(a => a.type === 'checking').length,
    savings: state.bankAccounts.filter(a => a.type === 'savings').length,
    credit_card: state.creditCards.length,
  };

  // Bank accounts locked: can't add any bank account type, OR total bank accounts
  // reached the combined limit (catches cash/investment types not tracked individually)
  const canAddChecking = isWithinLimit('max_checking_accounts', accountCounts.checking);
  const canAddSavings = isWithinLimit('max_savings_accounts', accountCounts.savings);
  const checkingLimit = getLimit('max_checking_accounts');
  const savingsLimit = getLimit('max_savings_accounts');
  const maxBankTotal = (checkingLimit === null || savingsLimit === null)
    ? Infinity
    : (checkingLimit + savingsLimit);
  const allAccountsLocked =
    (!canAddChecking && !canAddSavings) ||
    state.bankAccounts.length >= maxBankTotal;

  // Gated action: import statement
  const handleImportStatement = () => {
    if (importLocked) {
      setShowUpgradeModal(true);
    } else {
      state.setShowImportModal(true);
    }
  };

  // Gated action: import for specific account (from card buttons)
  const handleImportForAccount = (accountId: string) => {
    if (importLocked) {
      setShowUpgradeModal(true);
    } else {
      state.setSelectedAccountForImport(accountId);
      state.setShowImportModal(true);
    }
  };

  // Gated action: add account
  const handleAddAccount = () => {
    if (allAccountsLocked) {
      setShowUpgradeModal(true);
    } else {
      state.setSelectedAccount(null);
      state.setShowAccountModal(true);
    }
  };

  // Gated action: add credit card specifically
  const handleAddCreditCard = () => {
    if (creditCardLocked || !isWithinLimit('max_credit_cards', accountCounts.credit_card)) {
      setShowUpgradeModal(true);
    } else {
      state.setSelectedAccount(null);
      state.setShowAccountModal(true);
    }
  };

  const sharedAccountProps = {
    bankAccounts: state.bankAccounts,
    creditCards: state.creditCards,
    onAddAccount: handleAddAccount,
    onAddCreditCard: handleAddCreditCard,
    onEditAccount: (account: any) => {
      state.setSelectedAccount(account);
      state.setShowAccountModal(true);
    },
    onAddTransaction: (accountId: string) => {
      state.setDefaultTransactionAccountId(accountId);
      state.setShowTransactionModal(true);
    },
    onViewInvoice: (accountId: string) => {
      state.setSelectedCreditCardId(accountId);
      state.setShowInvoiceModal(true);
    },
    onImport: handleImportForAccount,
    onAddExpense: (accountId: string) => {
      state.setDefaultTransactionAccountId(accountId);
      state.setShowTransactionModal(true);
    },
    onAccountDragEnd: state.handleAccountDragEnd,
    onCreditCardDragEnd: state.handleCreditCardDragEnd,
    formatCurrency: state.formatCurrency,
    creditCardRefreshKey: state.creditCardRefreshKey,
    importLocked,
    creditCardLocked: creditCardLocked || !isWithinLimit('max_credit_cards', accountCounts.credit_card),
    allAccountsLocked,
  };

  const actionProps = {
    onNewTransaction: () => state.setShowTransactionModal(true),
    onImportStatement: handleImportStatement,
    onRefresh: state.fetchData,
    onViewTransaction: state.handleViewDueBill,
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <DashboardKpiHeader
        totalBalance={state.totalBalance}
        monthlyComparison={monthlyComparison}
        formatCurrency={state.formatCurrency}
        isLoading={analyticsLoading}
      />

      <DashboardAlertsBanner
        alerts={alerts}
        alertsLoading={alertsLoading}
        dismissAlert={dismissAlert}
      />

      <DashboardMobileTabs
        monthlyData={monthlyData}
        categoryData={categoryData}
        monthlyComparison={monthlyComparison}
        analyticsLoading={analyticsLoading}
        {...sharedAccountProps}
        {...actionProps}
      />

      <DashboardDesktopLayout
        monthlyData={monthlyData}
        categoryData={categoryData}
        monthlyComparison={monthlyComparison}
        analyticsLoading={analyticsLoading}
        {...sharedAccountProps}
        {...actionProps}
        importLocked={importLocked}
      />

      <DashboardFab
        onNewTransaction={actionProps.onNewTransaction}
        onImportStatement={handleImportStatement}
        onAddAccount={handleAddAccount}
        onFeedback={() => setShowFeedback(true)}
        importLocked={importLocked}
        accountLocked={allAccountsLocked}
      />

      <DashboardModals
        showTransactionModal={state.showTransactionModal}
        setShowTransactionModal={state.setShowTransactionModal}
        showImportModal={state.showImportModal}
        setShowImportModal={state.setShowImportModal}
        showInvoiceModal={state.showInvoiceModal}
        setShowInvoiceModal={state.setShowInvoiceModal}
        showAccountModal={state.showAccountModal}
        setShowAccountModal={state.setShowAccountModal}
        showEditTransactionModal={state.showEditTransactionModal}
        setShowEditTransactionModal={state.setShowEditTransactionModal}
        selectedCreditCardId={state.selectedCreditCardId}
        selectedAccountForImport={state.selectedAccountForImport}
        selectedAccount={state.selectedAccount}
        editingTransaction={state.editingTransaction}
        defaultTransactionAccountId={state.defaultTransactionAccountId}
        setDefaultTransactionAccountId={state.setDefaultTransactionAccountId}
        setSelectedAccountForImport={state.setSelectedAccountForImport}
        setEditingTransaction={state.setEditingTransaction}
        setCreditCardRefreshKey={state.setCreditCardRefreshKey}
        fetchData={state.fetchData}
        accountCounts={accountCounts}
      />

      <FeedbackDialog open={showFeedback} onOpenChange={setShowFeedback} />

      <UpgradeModal
        externalOpen={showUpgradeModal}
        onExternalClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default Dashboard;
